import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { NationalProductsDetails } from '@/types/nationalProducts.types';
import { NationalProductsSelection } from '@/types/nationalProductsSelection.types';
import { useSTKContext } from '@/context/context';

export const useNationalProducts = (enabled: boolean) => {
  const { apiClient } = useSTKContext();
  return useQuery<AxiosResponse<NationalProductsDetails[]>>({
    queryKey: ['nationalProducts'],
    queryFn: () => apiClient.get<NationalProductsDetails[]>('/products'),
    enabled,
  });
};

export const useUpdateNationalProductsIslock = () => {
  const { apiClient } = useSTKContext();
  return useMutation({
    mutationFn: async (requestBody: NationalProductsSelection[]) => {
      // Validate input data
      const validItems = requestBody.filter(
        (item) => item.variantId && typeof item.variantId === 'string' && item.variantId.trim() !== '',
      );

      if (validItems.length === 0) {
        throw new Error('No valid items to update');
      }

      // Map to backend DTO structure - ProductLockDto expects:
      // - variantId (UUID)
      // - isLock (Boolean) - maps to our priceLock
      // - mandatoryProductLock (Boolean)
      // - isVisible (Boolean)
      const payload = validItems.map((item) => ({
        variantId: item.variantId,
        isLock: Boolean(item.priceLock),
        mandatoryProductLock: Boolean(item.mandatoryProductLock),
        isVisible: item.isVisible !== undefined ? Boolean(item.isVisible) : undefined,
      }));

      try {
        const response = await apiClient.put('/products/update', payload);
        return response;
      } catch (error) {
        // Enhanced error handling
        if (error instanceof Error) {
          throw new Error(`Failed to update national products: ${error.message}`);
        }
        throw new Error('Failed to update national products: Unknown error');
      }
    },
    // Add retry logic for better reliability
    retry: (failureCount, error: unknown) => {
      // Retry up to 2 times for network errors, but not for validation errors
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (failureCount < 2 && !errorMessage.includes('No valid items')) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

export const identicalArrays = (originalData: NationalProductsDetails[], currentData: NationalProductsDetails[]) => {
  let identicalData = true;
  const stringToBoolean = (string: string | boolean) => (string === 'false' ? false : !!string);
  if (currentData === originalData) {
    identicalData = true;
  }
  if (currentData.length !== originalData.length) {
    return false;
  }
  for (let i = 0; i < currentData.length; i += 1) {
    const newLockBoolean = stringToBoolean(currentData[i].priceLock);
    const newMandatoryBoolean = stringToBoolean(currentData[i].mandatoryProductLock);
    const newIsVisibleBoolean = stringToBoolean(currentData[i].isVisible);
    if (
      newLockBoolean !== originalData[i].priceLock ||
      newMandatoryBoolean !== originalData[i].mandatoryProductLock ||
      newIsVisibleBoolean !== originalData[i].isVisible
    ) {
      return false;
    }
  }
  return identicalData;
};
